import { useCallback, useEffect, useRef, useState } from 'react';
import { logger } from 'src/logger';
import { ASANA_SHEPHERD_LAST_UPDATED_AT_S3_PREFIX, ASANA_SHEPHERD_S3_PREFIX, FinTechOpsCategories } from '../../FinTechOpsConstants';
import { fetchShepherdRisksRaw } from '../../oe-report/sections/fetchShepherdRisksRaw';
import { parseShepherdRisks, ShepherdRisk, ShepherdRiskRawTask } from '../../oe-report/sections/shepherdRisksData';
import { UseS3DatasetResult } from './useS3Dataset';

/**
 * Reads the shared Asana tasks S3 export and returns just the Shepherd risk rows.
 *
 * Unlike the other OE datasets, this does NOT go through `useS3Dataset`/`getS3Data`: those
 * discard an entire object if any line fails `JSON.parse`, and the Asana export currently
 * emits malformed `custom_fields` on exactly the Shepherd rows. `fetchShepherdRisksRaw`
 * reads the same objects but parses defensively (repair `\\"`→`\"`, skip anything still
 * bad). This is isolated to the Shepherd feed — no shared reader is modified — so no other
 * dataset's behaviour changes. See `fetchShepherdRisksRaw` for the full rationale.
 */
export const useShepherdRisksData = (): UseS3DatasetResult<ShepherdRisk[]> => {
  const [data, setData] = useState<ShepherdRisk[] | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  const refetch = useCallback(() => setReloadTick((t) => t + 1), []);

  // Guards a stale async result from overwriting a newer one after unmount / refetch.
  const runIdRef = useRef(0);

  useEffect(() => {
    const runId = ++runIdRef.current;
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const { rows, lastUpdatedAt: updatedAt } = await fetchShepherdRisksRaw(
          ASANA_SHEPHERD_S3_PREFIX,
          ASANA_SHEPHERD_LAST_UPDATED_AT_S3_PREFIX,
          reloadTick > 0 // a manual refetch bypasses the cache
        );
        if (cancelled || runId !== runIdRef.current) return;
        setData(parseShepherdRisks(rows as ShepherdRiskRawTask[]));
        setLastUpdatedAt(updatedAt);
      } catch (err) {
        if (cancelled || runId !== runIdRef.current) return;
        logger.error(`Unable to load ${FinTechOpsCategories.shepherdRisks} data`, err as Error);
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        if (!cancelled && runId === runIdRef.current) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [reloadTick]);

  return { data, loading, error, lastUpdatedAt, refetch };
};
